import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { sortBy } from 'ramda';
import isTouchDevice from 'is-touch-device';
import ChevronLeftIcon from '@moda/icons/chevron-left-20';
import ChevronRightIcon from '@moda/icons/chevron-right-20';
import { space } from '@moda/tokens';
import { Clickable } from '@moda/om';
import { compact, titleize, useIsScrollable, useNode } from '@moda/portal-stanchions';
import { usePLPParams } from '../../../../hooks/usePLPParams';
import { useCategoryTree } from '../../../../hooks/useCategoryTree';
import { Image } from '../../../Image';
import { useVisualStyleFiltersFeatureFlag } from '../useVisualStyleFiltersFeatureFlag';
import { VisualStyleFilterShapeLayout } from '../VisualStyleFilterShapeLayout';
import { VisualStyleFilterAllStyles } from '../VisualStyleFilterAllStyles';
import { VisualStyleFilter } from '../VisualStyleFilter';
import { Props } from '../ProductListingVisualStyleFilters';

import './VisualStyleFilters.scss';

const MEDIUM_SIZE = 4;
const ARROW_SIZE = space.scale[MEDIUM_SIZE];

export const VisualStyleFilters: React.FC<Props> = ({ filters, categoryTree }) => {
  const { ref, node } = useNode();
  const { isScrollable, isMaxLeft, isMaxRight, scrollLeft, scrollRight } = useIsScrollable(node);

  const isNotTouchDevice = !isTouchDevice();
  const [fallbacks, setFallbacks] = useState<string[]>([]);

  const sortedFilters = useMemo(
    () => sortBy(_ => _.totalItems || 0, compact(filters)).reverse(),
    [filters]
  );

  const { selectedCategoryNode } = useCategoryTree(categoryTree);

  const {
    params: { attributes },
    replaceFilter
  } = usePLPParams();
  const { isDesktopEnabled, isMobileEnabled, path: basePath } = useVisualStyleFiltersFeatureFlag();

  if (!isDesktopEnabled && !isMobileEnabled) return null;

  return (
    <div
      className={classNames('VisualStyleFilters__wrapper', {
        'VisualStyleFilters__wrapper--scrollable': isScrollable,
        'VisualStyleFilters__wrapper--hidden-on-mobile': !isMobileEnabled,
        'VisualStyleFilters__wrapper--hidden-on-desktop': !isDesktopEnabled
      })}
    >
      {isScrollable && (
        <Clickable
          className={classNames('VisualStyleFilters__arrow', 'VisualStyleFilters__arrow--left', {
            'VisualStyleFilters__arrow--disabled': isMaxLeft,
            'VisualStyleFilters__arrow--hidden': !isNotTouchDevice
          })}
          onClick={scrollLeft}
          onKeyPress={scrollLeft}
          tabIndex={isMaxRight ? -1 : 0}
        >
          <ChevronLeftIcon width={ARROW_SIZE} height={ARROW_SIZE} />
        </Clickable>
      )}

      <div className="VisualStyleFilters" ref={isNotTouchDevice ? ref : undefined}>
        <VisualStyleFilterAllStyles categoryName={selectedCategoryNode.name} />

        {sortedFilters.map(filter => {
          if (!filter?.slug || !filter?.name) return null;

          const name = titleize(
            filter.name.replace(/-/g, ' ').replace(selectedCategoryNode.name || '', '')
          );

          return (
            <VisualStyleFilter
              isSelected={attributes.includes(filter.slug || '')}
              key={filter.slug}
              onClick={() => filter.slug && replaceFilter('attributes', filter.slug)}
              name={name}
            >
              {fallbacks.includes(filter.slug) ? (
                <VisualStyleFilterShapeLayout>{name}</VisualStyleFilterShapeLayout>
              ) : (
                <Image
                  alt={name}
                  className="VisualStyleFilters__image"
                  src={`${basePath}/${filter.slug}.jpg`}
                  srcMode="static"
                  onError={() =>
                    setFallbacks(prev => (filter.slug ? [...prev, filter.slug] : prev))
                  }
                />
              )}
            </VisualStyleFilter>
          );
        })}
      </div>

      {isScrollable && (
        <Clickable
          className={classNames('VisualStyleFilters__arrow', 'VisualStyleFilters__arrow--right', {
            'VisualStyleFilters__arrow--disabled': isMaxRight,
            'VisualStyleFilters__arrow--hidden': !isNotTouchDevice
          })}
          onClick={scrollRight}
          onKeyPress={scrollRight}
          tabIndex={isMaxRight ? -1 : 0}
        >
          <ChevronRightIcon width={ARROW_SIZE} height={ARROW_SIZE} />
        </Clickable>
      )}
    </div>
  );
};
